import * as React from 'react'

import type { CostsSummary } from '@sdr-agend/admin-schemas'
import { formatCurrencyUSD } from '@/lib/format'

/**
 * "Custos" (Story 10.6, 2026-07-30, user request, AC3+AC4) — ESTIMATIVA de
 * gasto com IA (OpenAI/Claude/Gemini, quebrado por provedor) + WhatsApp API
 * (Meta) no período selecionado. Explicitamente NÃO é custo de
 * infraestrutura de servidor (decisão de produto documentada na Story) e
 * NÃO é um valor de billing exato — ver `queryCostsSummary` no backend e a
 * tabela `whatsapp-pricing.ts` para o porquê.
 */
export interface CostsSummaryPanelProps {
  data: CostsSummary
}

/**
 * QA fix (2026-07-30) — `provider` comes from the backend as the lowercase
 * `resource_type` suffix (`openai`/`claude`/`gemini`, see
 * `queryCostsSummary`'s docstring). CSS `capitalize` alone would render
 * "Openai" — wrong casing for a proper noun — so known providers get an
 * explicit display name; anything unrecognized falls back to CSS
 * `capitalize` (still readable, just not guaranteed brand-perfect casing)
 * instead of crashing or hiding the row.
 */
const PROVIDER_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  openai: 'OpenAI',
  claude: 'Claude',
  gemini: 'Gemini',
}

function providerDisplayName(provider: string): string {
  return PROVIDER_DISPLAY_NAMES[provider] ?? provider
}

export function CostsSummaryPanel({ data }: CostsSummaryPanelProps): React.ReactElement {
  const hasData = data.totalUsd > 0 || data.whatsapp.messagesSent > 0 || data.ai.byProvider.length > 0

  return (
    <div data-testid="costs-summary-panel">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-t-2 border-t-slate-500 p-3">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Total estimado
          </p>
          <p className="text-2xl font-semibold tabular-nums" data-testid="costs-total">
            {formatCurrencyUSD(data.totalUsd)}
          </p>
        </div>
        <div className="rounded-lg border border-t-2 border-t-violet-500 p-3">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
            IA (todos os provedores)
          </p>
          <p className="text-2xl font-semibold tabular-nums" data-testid="costs-ai-total">
            {formatCurrencyUSD(data.ai.totalUsd)}
          </p>
        </div>
        <div className="rounded-lg border border-t-2 border-t-sky-500 p-3">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
            WhatsApp ({data.whatsapp.messagesSent.toLocaleString('pt-BR')} msgs)
          </p>
          <p className="text-2xl font-semibold tabular-nums" data-testid="costs-whatsapp-total">
            {formatCurrencyUSD(data.whatsapp.totalUsd)}
          </p>
        </div>
      </div>

      {!hasData && (
        <p className="mt-3 text-[11px] text-muted-foreground" data-testid="costs-empty-note">
          Nenhum custo registrado no período selecionado.
        </p>
      )}

      {data.ai.byProvider.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            IA por provedor
          </p>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="pb-1.5 pr-3 font-medium">Provedor</th>
                <th className="pb-1.5 pr-3 text-right font-medium">Chamadas</th>
                <th className="pb-1.5 text-right font-medium">Custo estimado</th>
              </tr>
            </thead>
            <tbody>
              {data.ai.byProvider.map((row) => (
                <tr key={row.provider} className="border-b last:border-0" data-testid={`costs-provider-${row.provider}`}>
                  <td className="py-1.5 pr-3 font-medium capitalize">{providerDisplayName(row.provider)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">
                    {row.calls.toLocaleString('pt-BR')}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{formatCurrencyUSD(row.totalUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 text-[11px] text-muted-foreground">
        Estimativa, não é o valor exato cobrado pela OpenAI/Claude/Gemini/Meta — não inclui custo
        de infraestrutura de servidor. Custo de WhatsApp é rastreado só a partir da entrada desta
        funcionalidade no ar (sem histórico retroativo).
      </p>
    </div>
  )
}

export default CostsSummaryPanel
